// PHASE_1 / B2 — the attendance (me-attendance) Content component (ADR-014 §2).
//
// AttendanceContent is a PURE, shell-agnostic Content component (the *Content
// convention B0 establishes). The SAME component renders under the SINGLE
// parameterized AppShell in BOTH contexts: /me/attendance (the employee's own
// clock + entries, employee hat) and its admin twin. One implementation; the
// role-swap is structural (ADR-014 §2).
//
// THE SERVER DOES NOT SCOPE THESE ROWS TO THE CALLER, the tier does: only
// employee_self is narrowed to its own entries, while executive_broad is
// answered the whole tenant and manager_subtree its subtree. A "my attendance"
// screen that asks without an employee id therefore shows an İK user their
// colleagues' punches as their own, and points the clock toggle by whoever
// happens to be clocked in (E22). So this screen resolves WHO the caller is
// first (`/v1/me`), and every read below is pinned to that id. AttendanceBody
// takes the id as a required prop rather than an optional one so a read cannot
// be written without it.
//
// Faz 8.6 / Batch A8 restyles the VISUAL layer to the novem attendance language
// (NovemHR-ekranlar emp-attendance.jsx — serif PageHeader+em, a StatCard strip
// [net hours / open punch / entries / sources], the web-punch card, a novem
// table with Badge status tones). The DATA layer is otherwise untouched: the
// SAME web punch toggle (clock in/out via the authoritative open-punch lookup)
// + the caller's own time-entry list via DataTable + useKeysetQuery
// (BUILD_PLAN §5 keyset, never OFFSET). De-coupled per gap §9.1: NO İSTON
// dual-ERP / Moodle / D365 binding — the punch posts to the native NovemAI
// time route. Mobile/kiosk punch + GPS/geofence are P6 (the route rejects those
// sources); this screen does web punch only.
//
// The mockup's weekly grid, correction requests, location/IP, and monthly heat
// map have NO backend on the entries route — those rows/affordances are not
// faked (port rule 8 — recorded in notes). The KPIs read the LOADED keyset
// window honestly (a "+" suffix flags more pages, the dashboard convention).
//
// Hours are DECIMALS (`_hours`), never money — nothing here goes through <Money>
// (Phase 1 money/hours boundary). They are formatted with the locale's decimal
// separator via formatHours.

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBlocker, useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock3, Fingerprint, ListChecks, Radio } from "lucide-react";

import {
  Badge,
  Banner,
  Button,
  Card,
  DataTable,
  Empty,
  ErrorState,
  Loading,
  PageHeader,
  StatCard,
  type DataTableColumn,
} from "@novemai/components";

import { EnvelopeError, errorMessage } from "@/api/errors";
import { useKeysetQuery } from "@/api/keyset";
import { useTimeZone } from "@/api/use-time-zone";
import {
  fetchOwnActiveEntry,
  fetchOwnTimeEntriesPage,
  fetchTimeCaller,
  punch,
  type TimeEntryRow,
} from "@/features/time/time-api";
import { GeofenceReviewDialog } from "@/features/time/geofence-review-dialog";
import { MyShiftsCard } from "@/features/time/my-shifts-card";
import { MyTimesheetsCard } from "@/features/time/my-timesheets-card";
import { ENTRY_STATUS_TONE } from "@/features/time/time-status";
import { formatCountWithMore } from "@/lib/count";
import { formatHours } from "@/lib/hours";

/** The browser's IANA timezone — the punch carries it so the entry is tz-anchored. */
function resolveTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

interface PendingPunchIntent {
  readonly body: {
    readonly source: "web";
    readonly direction: "in" | "out";
    readonly timezone: string;
  };
  readonly idempotencyKey: string;
}

/** A known 4xx means the server definitely did not leave an unknown punch
 * result for this intent. Network/unknown and 5xx failures remain ambiguous:
 * the server may already have committed, so their exact key+body must replay. */
function isDefinitivePunchFailure(error: unknown): boolean {
  return error instanceof EnvelopeError && error.httpStatus >= 400 && error.httpStatus < 500;
}

export function AttendanceContent(): React.ReactElement {
  const { t } = useTranslation();
  const caller = useQuery({ queryKey: ["time", "caller"], queryFn: fetchTimeCaller });
  const employeeId = caller.data?.employee_id ?? null;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader
        title={
          <>
            {t("time.attendance.title_lead")} <em>{t("time.attendance.title_em")}</em>
          </>
        }
        description={t("time.attendance.lead")}
      />

      {caller.isLoading ? (
        <Loading label={t("time.loading")} rows={5} />
      ) : caller.isError ? (
        <ErrorState title={t("time.error_title")} description={errorMessage(caller.error, t)} />
      ) : employeeId === null ? (
        // An account with no employee record has no attendance of its own. Asking
        // the list anyway would be answered by tier, i.e. with other people's rows.
        <Empty
          title={t("time.attendance.no_employee_title")}
          description={t("time.attendance.no_employee_description")}
        />
      ) : (
        <AttendanceBody employeeId={employeeId} />
      )}
    </div>
  );
}

function AttendanceBody({ employeeId }: { readonly employeeId: string }): React.ReactElement {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  // The "Çalışma programı yayımlandı" notification links here with the id of
  // the roster that was published.
  const scheduleId = searchParams.get("schedule_id");
  const [geofencePending, setGeofencePending] = useState(false);
  const [geofenceEntry, setGeofenceEntry] = useState<TimeEntryRow | null>(null);
  // Set by a clock-out the server flagged for manager review, so the person who
  // just closed a 1,521-hour entry is told at the moment it happens rather than
  // finding a badge on a row later (E6).
  const [flaggedPunch, setFlaggedPunch] = useState(false);

  // The employee id is part of both keys: a cached list read for one person must
  // never be served to another sharing the browser session. Invalidation below
  // still targets the shorter prefixes, which match these.
  const query = useKeysetQuery<TimeEntryRow>({
    queryKey: ["time", "entries", "own", employeeId],
    fetchPage: (cursor) => fetchOwnTimeEntriesPage(employeeId, cursor),
  });

  // The caller's open punch drives the toggle — an AUTHORITATIVE single-row
  // lookup (status=active&limit=1), NOT a scan of the first keyset page. With
  // >1 page the open punch can be off-page; reading it directly keeps the
  // clock-in/clock-out label correct regardless of how many entries exist.
  const activeQuery = useQuery<TimeEntryRow | null>({
    queryKey: ["time", "active-entry", employeeId],
    queryFn: () => fetchOwnActiveEntry(employeeId),
  });
  const openEntry = activeQuery.data ?? null;

  // The browser owns an immutable intent across an ambiguous response. The
  // server's legacy toggle is intentionally guarded with an explicit direction:
  // retrying a lost clock-in response must replay the clock-in rather than
  // observe the now-open row and toggle it back out.
  const pendingPunch = useRef<PendingPunchIntent | null>(null);
  const [hasPendingPunchIntent, setHasPendingPunchIntent] = useState(false);

  // The route owns the exact key/body for a response-ambiguous punch. Leaving
  // it would discard that intent; on re-entry, a newly observed opposite state
  // could turn a retry of clock-in into an unrelated clock-out.
  const punchBlocker = useBlocker(hasPendingPunchIntent || geofencePending);
  useEffect(() => {
    if (!hasPendingPunchIntent && punchBlocker.state === "blocked") {
      punchBlocker.reset();
    }
  }, [hasPendingPunchIntent, punchBlocker]);

  // React Router protects in-app transitions; this covers refresh, tab close,
  // browser Back that leaves the SPA, and cross-origin navigation. Browsers
  // intentionally render their own generic wording for beforeunload prompts.
  useEffect(() => {
    if (!hasPendingPunchIntent && !geofencePending) return;
    const preventUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", preventUnload);
    return () => window.removeEventListener("beforeunload", preventUnload);
  }, [hasPendingPunchIntent, geofencePending]);

  const ensurePendingPunchIntent = (): PendingPunchIntent => {
    const current = pendingPunch.current;
    if (current !== null) return current;

    const next: PendingPunchIntent = {
      body: {
        source: "web",
        direction: openEntry === null ? "in" : "out",
        timezone: resolveTimezone(),
      },
      idempotencyKey: crypto.randomUUID(),
    };
    pendingPunch.current = next;
    setHasPendingPunchIntent(true);
    return next;
  };

  const settlePendingPunchIntent = (intent: PendingPunchIntent): void => {
    if (pendingPunch.current !== intent) return;
    pendingPunch.current = null;
    setHasPendingPunchIntent(false);
  };

  // The clock in/out toggle. The route decides which side fired (an open punch →
  // clock-out; none → clock-in) and returns `action`; the screen invalidates
  // both the entry list (so the new/updated row appears) and the authoritative
  // open-punch lookup (so the toggle label flips).
  const punchMutation = useMutation({
    mutationFn: (intent: PendingPunchIntent) =>
      punch(intent.body, intent.idempotencyKey),
    onSuccess: (result, intent) => {
      settlePendingPunchIntent(intent);
      setFlaggedPunch(result.action === "clock_out" && result.entry.requires_review === true);
      void queryClient.invalidateQueries({ queryKey: ["time", "entries"] });
      void queryClient.invalidateQueries({ queryKey: ["time", "active-entry"] });
      // The week below and the sheet totals are computed from these punches, and a
      // punch on a day of approved leave raises a warning there: without this the
      // card shows the state from before the punch until the page is reloaded.
      void queryClient.invalidateQueries({ queryKey: ["time", "week-days"] });
      void queryClient.invalidateQueries({ queryKey: ["time", "timesheets"] });
    },
    onError: (error, intent) => {
      if (!isDefinitivePunchFailure(error)) return;
      settlePendingPunchIntent(intent);
      // A definitive state/validation denial means the cached active-entry
      // display may be stale. Refresh it before the next independent intent.
      void queryClient.invalidateQueries({ queryKey: ["time", "entries"] });
      void queryClient.invalidateQueries({ queryKey: ["time", "active-entry"] });
    },
  });

  const toggleLabel = openEntry ? t("time.action.clock_out") : t("time.action.clock_in");

  // KPI strip — honest counts over the LOADED keyset window (the request pins
  // the rows to this employee). A "+" suffix flags that more pages remain.
  const loggedMinutes = useMemo(
    () => query.items.reduce((sum, r) => sum + (r.duration_minutes ?? 0), 0),
    [query.items],
  );
  const loggedHours = `${formatHours(loggedMinutes / 60, i18n.language) ?? "0"}${
    query.hasNextPage && loggedMinutes > 0 ? "+" : ""
  }`;
  const sourceCount = useMemo(
    () => new Set(query.items.map((r) => r.source)).size,
    [query.items],
  );

  // "Çıkış yaptınız" is only true for someone who clocked out TODAY; a person
  // who never punched (or last punched days ago) is neither clocked in nor
  // clocked out, and must not be told they left (E40). Days compare in the same
  // zone the timestamps are drawn in.
  const today = dayKey(new Date().toISOString(), timeZone);
  const clockedOutToday =
    openEntry === null &&
    query.items.some((r) => r.end_at !== null && dayKey(r.end_at, timeZone) === today);
  const punchStatusLoaded = !activeQuery.isLoading && !query.isLoading;

  const columns: readonly DataTableColumn<TimeEntryRow>[] = [
    {
      id: "start",
      header: t("time.column.start"),
      cell: (row): string => formatInstant(i18n.language, timeZone, row.start_at),
    },
    {
      id: "end",
      header: t("time.column.end"),
      cell: (row): string =>
        row.end_at ? formatInstant(i18n.language, timeZone, row.end_at) : t("time.cell.open"),
    },
    {
      id: "hours",
      header: t("time.column.hours"),
      align: "end",
      // Decimal hours from duration_minutes — NOT money.
      cell: (row): React.ReactNode => {
        const hours =
          row.duration_minutes !== null
            ? formatHours(row.duration_minutes / 60, i18n.language)
            : null;
        return hours !== null ? (
          <span className="font-mono tabular-nums text-[var(--fg-primary)]">{hours}</span>
        ) : (
          <span className="text-[var(--fg-muted)]">{t("time.cell.dash")}</span>
        );
      },
    },
    {
      id: "source",
      header: t("time.column.source"),
      cell: (row): string => t(`time.source.${row.source}`),
    },
    {
      id: "status",
      header: t("time.column.status"),
      cell: (row): React.ReactNode => (
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge tone={ENTRY_STATUS_TONE[row.status] ?? "neutral"}>
            {t(`time.entry_status.${row.status}`)}
          </Badge>
          {row.requires_review === true ? (
            <Badge tone="warning">{t("time.entry.review_badge")}</Badge>
          ) : null}
        </div>
      ),
    },
    {
      id: "geofence",
      header: t("time.geofence.title"),
      cell: (row): React.ReactNode => row.geofence_match ? <Button size="sm" variant="secondary" onClick={() => setGeofenceEntry(row)}>{t(row.employee_id === employeeId && (row.geofence_match.clock_in?.reason_required || row.geofence_match.clock_out?.reason_required) ? "time.geofence.add_reason" : "time.geofence.view")}</Button> : t("time.cell.dash"),
    },
  ];

  const shiftsCard = <MyShiftsCard employeeId={employeeId} scheduleId={scheduleId} />;

  return (
    <>
      {/* Someone who followed the "schedule published" notification came for the
          roster, so it leads the page; otherwise it sits under the punch card. */}
      {scheduleId !== null ? shiftsCard : null}

      {/* KPI strip — novem StatCard row (net hours / open punch / entries / sources) */}
      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <StatCard
          label={t("time.attendance.stat.logged")}
          value={loggedHours}
          delta={t("time.attendance.stat.logged_sub")}
          deltaDirection="flat"
          tone="accent"
          icon={<Clock3 size={18} />}
        />
        <StatCard
          label={t("time.attendance.stat.open")}
          value={openEntry ? "1" : "0"}
          delta={t("time.attendance.stat.open_sub")}
          deltaDirection="flat"
          tone={openEntry ? "warning" : "neutral"}
          icon={<Fingerprint size={18} />}
        />
        <StatCard
          label={t("time.attendance.stat.entries")}
          value={formatCountWithMore(query.items.length, query.hasNextPage)}
          delta={t("time.attendance.stat.entries_sub")}
          deltaDirection="flat"
          tone="info"
          icon={<ListChecks size={18} />}
        />
        <StatCard
          label={t("time.attendance.stat.sources")}
          value={String(sourceCount)}
          delta={t("time.attendance.stat.sources_sub")}
          deltaDirection="flat"
          tone="neutral"
          icon={<Radio size={18} />}
        />
      </div>

      {/* Web punch card */}
      <Card>
        <div className="flex items-center justify-between gap-4 p-5">
          <div className="flex items-center gap-3">
            <div
              className="flex h-11 w-11 items-center justify-center rounded-[10px]"
              style={{
                background: openEntry
                  ? "color-mix(in srgb, var(--state-warning) 8%, var(--bg-raised))"
                  : "var(--accent-subtle-bg)",
              }}
              aria-hidden="true"
            >
              <Fingerprint
                size={20}
                color={openEntry ? "var(--state-warning)" : "var(--accent)"}
              />
            </div>
            <div>
              {punchStatusLoaded ? (
                <p className="font-serif text-[16px] text-[var(--fg-primary)]">
                  {openEntry
                    ? t("time.punch.on_clock")
                    : clockedOutToday
                      ? t("time.punch.off_clock")
                      : t("time.punch.not_started")}
                </p>
              ) : null}
              {openEntry && (
                <p className="font-sans text-[13px] text-[var(--fg-muted)]">
                  {t("time.punch.since", {
                    at: formatInstant(i18n.language, timeZone, openEntry.start_at),
                  })}
                </p>
              )}
            </div>
          </div>
          <Button
            variant="primary"
            size="md"
            disabled={punchMutation.isPending}
            onClick={(): void => {
              punchMutation.mutate(ensurePendingPunchIntent());
            }}
          >
            {toggleLabel}
          </Button>
        </div>
      </Card>

      {punchMutation.isError && (
        <Banner severity="danger" title={t("time.punch.error_title")}>
          {errorMessage(punchMutation.error, t)}
        </Banner>
      )}

      {flaggedPunch && !punchMutation.isError ? (
        <Banner severity="warning" title={t("time.punch.review_title")}>
          {t("time.punch.review_body")}
        </Banner>
      ) : null}

      {scheduleId === null ? shiftsCard : null}

      <MyTimesheetsCard employeeId={employeeId} />

      {query.isLoading ? (
        <Loading label={t("time.loading")} rows={5} />
      ) : query.isError ? (
        <ErrorState
          title={t("time.error_title")}
          description={errorMessage(query.error, t)}
        />
      ) : query.items.length > 0 ? (
        // The shifts card above also draws a data-table, and the tour's generic
        // `table` anchor takes the FIRST one: without this override the step that
        // describes the clock records would frame the shifts.
        <div data-tour="table">
          <DataTable<TimeEntryRow>
            caption={t("time.table_caption")}
            columns={columns}
            rows={query.items}
            rowKey={(row): string => row.entry_id}
            hasNextPage={query.hasNextPage}
            onLoadMore={query.fetchNextPage}
            isFetchingNextPage={query.isFetchingNextPage}
            loadMoreLabel={t("time.load_more")}
          />
        </div>
      ) : (
        <Empty title={t("time.empty_title")} description={t("time.empty_description")} />
      )}
      {geofenceEntry ? <GeofenceReviewDialog onPendingChange={setGeofencePending} entryId={geofenceEntry.entry_id} mode={geofenceEntry.employee_id === employeeId && ["active", "submitted"].includes(geofenceEntry.status) ? "worker_reason" : "read"} onClose={() => setGeofenceEntry(null)} /> : null}
    </>
  );
}

/**
 * Bir ISO anını çalışanın etkin dilimindeki duvar saati olarak çizer.
 * Yalnız gösterim.
 *
 * Dilim tarayıcıdan DEĞİL sunucudan gelir (SEM-6938): sunucu bu damgaları
 * kiracının dilimindeki gün sınırına göre haftalara bağlıyor, tarayıcı dilimi
 * gösterilen saati o gruplandırmadan koparırdı. Yurt dışındaki bir çalışan
 * kendi saatini görür, satır ise başka bir günün altında dururdu.
 *
 * `resolveTimezone()` yukarıda AYRI bir soruyu cevaplar ve tarayıcıyı okumaya
 * devam eder: o, damganın nerede basıldığıdır.
 */
function formatInstant(locale: string, timeZone: string, iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone,
    }).format(d);
  } catch {
    // Intl bilinmeyen dilimde RangeError atar; satırı düşürmek yerine UTC çiz.
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "UTC",
    }).format(d);
  }
}

/**
 * Bir anın etkin dilimdeki takvim günü (YYYY-MM-DD). Yalnız "aynı gün mü"
 * karşılaştırması içindir; `en-CA` sırf sıralanabilir sayısal biçimi için
 * seçildi, gösterilmez. Bilinmeyen dilimde formatInstant gibi UTC'ye düşer.
 */
function dayKey(iso: string, timeZone: string): string {
  const d = new Date(iso);
  const format = (zone: string): string =>
    new Intl.DateTimeFormat("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: zone,
    }).format(d);
  try {
    return format(timeZone);
  } catch {
    return format("UTC");
  }
}
